import React from 'react';
import clsx from 'clsx';
import {
    Clock,
    CheckCircle,
    Send,
    Eye,
    ThumbsUp,
    ThumbsDown,
} from 'lucide-react';
import { PfmeaItem, ApprovalSignature } from '../../types';

interface PfmeaListItemProps {
    item: PfmeaItem;
    isDark: boolean;
    hasPermission: (p: string) => boolean;
    processing: boolean;
    calculateRPN: (s: number, o: number, d: number) => number;
    formatDate: (d?: string) => string;
    getLastAction: (item: PfmeaItem) => ApprovalSignature | null;
    onSubmit: (item: PfmeaItem) => void;
    onApproveClick: (item: PfmeaItem, action: 'approve' | 'reject') => void;
    onViewDetails: (item: PfmeaItem) => void;
}

const PfmeaListItem: React.FC<PfmeaListItemProps> = ({
    item, isDark, hasPermission, processing, calculateRPN, formatDate, getLastAction, onSubmit, onApproveClick, onViewDetails
}) => {
    const rpn = calculateRPN(item.severity, item.occurrence, item.detection);
    const lastAction = getLastAction(item);

    return (
        <div className={clsx('p-4 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors', isDark ? 'bg-slate-900' : 'bg-white')}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-slate-400">{item.processStepNumber}</span>
                        <span className={clsx(
                            'px-2 py-0.5 rounded text-xs font-medium',
                            item.status === 'draft' ? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' :
                                item.status === 'review' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                                    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        )}>{item.status}</span>
                        <span className={clsx('px-2 py-0.5 rounded text-xs font-bold', item.actionPriority === 'H' ? 'bg-red-500 text-white' : item.actionPriority === 'M' ? 'bg-amber-500 text-white' : 'bg-green-500 text-white')}>
                            {item.actionPriority}
                        </span>
                        <span className={clsx('text-xs font-mono', rpn >= 100 ? 'text-red-500 font-bold' : rpn >= 50 ? 'text-amber-500' : 'text-green-500')}>
                            RPN: {rpn}
                        </span>
                    </div>
                    <h3 className={clsx('font-medium truncate', isDark ? 'text-white' : 'text-slate-800')}>{item.processStepName || 'Untitled Step'}</h3>
                    <p className="text-sm text-slate-500 truncate">{item.failureMode} → {item.failureEffect}</p>
                    {item.status === 'approved' && item.approvedBy && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                            <CheckCircle size={12} /> Approved by {item.approvedBy} on {formatDate(item.approvedAt?.toString())}
                        </div>
                    )}
                    {lastAction && item.status === 'review' && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                            <Clock size={12} /> Submitted by {lastAction.user} on {formatDate(lastAction.timestamp)}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {item.status === 'draft' && hasPermission('pfmea.edit') && (
                        <button onClick={() => onSubmit(item)} disabled={processing} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
                            <Send size={14} /> Submit
                        </button>
                    )}
                    {item.status === 'review' && hasPermission('pfmea.approve') && (
                        <>
                            <button onClick={() => onApproveClick(item, 'approve')} disabled={processing} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"><ThumbsUp size={14} /> Approve</button>
                            <button onClick={() => onApproveClick(item, 'reject')} disabled={processing} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"><ThumbsDown size={14} /> Reject</button>
                        </>
                    )}
                    <button onClick={() => onViewDetails(item)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><Eye size={16} /></button>
                </div>
            </div>
        </div>
    );
};

export default PfmeaListItem;
